// src/pages/admin/AdminDailyPdf.js
import React, { useState } from "react";
import { API_BASE, fileHref } from "../../services/apiClient";
import "./DailyPdfGenerator.css";
import DateInput from "../../components/site/DateInput";
import { formatDate, formatDateTime } from "../../utils/dateFormat";
import { PageNav, PageSummary, usePaging } from "../../components/admin/Pager";

function AdminDailyPdf() {
  const [selectedDate, setSelectedDate] = useState("");
  const [items, setItems] = useState([]);
  const paging = usePaging(items, "mng_dailypdf_page_size", "");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  const handleLoadAnnouncements = async () => {
    if (!selectedDate) {
      alert("กรุณาเลือกวันที่หนังสือพิมพ์");
      return;
    }

    setLoading(true);
    setStatus("กำลังโหลดใบประกาศ...");
    try {
      const res = await fetch(
        `${API_BASE}/api/announcements/by-date?date=${selectedDate}`
      );
      const data = await res.json();
      if (!data.success) {
        setItems([]);
        setStatus(data.message || "โหลดไม่สำเร็จ");
        return;
      }
      setItems(data.items || []);
      setStatus(
        data.items && data.items.length
          ? `พบใบประกาศ ${data.items.length} ใบ`
          : "ไม่พบใบประกาศในวันนี้"
      );
    } catch (err) {
      console.error(err);
      setStatus("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  // inline date picker per card (dd-mm-yyyy box) instead of a raw "YYYY-MM-DD" prompt
  const [dateEdit, setDateEdit] = useState({ id: null, value: "" });
  const [pdfLink, setPdfLink] = useState("");

  const handleChangeIssueDate = async (itemId, newDate) => {
    if (!newDate) {
      alert("กรุณาเลือกวันที่ใหม่");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/api/announcement/${itemId}/issue-date`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ issueDate: newDate }),
        }
      );
      const data = await res.json();
      if (!data.success) {
        alert("เปลี่ยนวันที่ไม่สำเร็จ: " + (data.message || data.error));
        return;
      }
      alert("เปลี่ยนวันที่สำเร็จ");
      setDateEdit({ id: null, value: "" });
      handleLoadAnnouncements();
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการเปลี่ยนวันที่");
    }
  };

  const handleDeleteAnnouncement = async (item) => {
    const ok = window.confirm(
      `ยืนยันลบใบประกาศรหัส ${item.id} ?\n(ไฟล์รูปจะถูกลบออกจากระบบด้วย)`
    );
    if (!ok) return;

    try {
      const res = await fetch(`${API_BASE}/api/announcement/${item.id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!data.success) {
        alert("ลบไม่สำเร็จ: " + (data.message || data.error));
        return;
      }
      alert("ลบใบประกาศสำเร็จ");
      handleLoadAnnouncements();
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการลบใบประกาศ");
    }
  };

  const handleGeneratePdf = async () => {
    if (!selectedDate) {
      alert("กรุณาเลือกวันที่หนังสือพิมพ์");
      return;
    }

    setStatus("กำลังสร้าง PDF...");
    setPdfLink("");
    try {
      const res = await fetch(`${API_BASE}/api/daily-pdf/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: selectedDate }),
      });
      const data = await res.json();
      if (!data.success) {
        setStatus("สร้าง PDF ไม่สำเร็จ: " + (data.message || data.error));
        return;
      }

      setStatus("สร้าง PDF สำเร็จ!");
      // a link, not window.open: after an await the popup blocker stops window.open and the file never showed
      if (data.pdfUrl) setPdfLink(fileHref(data.pdfUrl));
    } catch (err) {
      console.error(err);
      setStatus("เกิดข้อผิดพลาดในการสร้าง PDF");
    }
  };

  return (
    <div className="admin-daily-pdf-page">
      <h1>สร้างไฟล์ PDF ประกาศรายวัน</h1>

      {/* The old cover template no longer prints: this button now makes the same newspaper as the edition page
          (masthead, issue number, articles, notices 4 per page, back cover) — owner, 28-10-2569 */}
      <section className="adp-cover-note">
        <p>
          PDF ที่สร้างจากหน้านี้เป็นหนังสือพิมพ์รูปแบบเดียวกับหน้า “หนังสือพิมพ์รายวัน” บนเว็บ (หัวหนังสือพิมพ์ + QR, ฉบับที่, ข่าว,
          ประกาศ 4 ช่องต่อหน้า และปกหลัง) แก้ไขข้อความหัว / ท้ายหนังสือพิมพ์ได้ที่{" "}
          <a href="#/admin/content">แก้ไขเนื้อหาเว็บ → หัว / ท้ายหนังสือพิมพ์</a>
        </p>
      </section>

      <div className="adp-controls">
        <label>
          วันที่ประกาศ (วว-ดด-ปปปป):{" "}
          <DateInput
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </label>

        <button onClick={handleLoadAnnouncements}>โหลดใบประกาศของวันนี้</button>

        <button onClick={handleGeneratePdf} style={{ marginLeft: "8px" }}>
          สร้าง PDF สำหรับวันนี้
        </button>
      </div>

      {status && <div className="adp-status">{status}</div>}
      {pdfLink && (
        <div className="adp-status">
          <a href={pdfLink} target="_blank" rel="noreferrer">เปิดไฟล์ PDF ที่สร้าง ↗</a>
        </div>
      )}

      {!loading && items.length > 0 && <PageSummary paging={paging} />}
      <div className="adp-list">
        {loading && <div>กำลังโหลด...</div>}

        {!loading && items.length === 0 && (
          <div style={{ marginTop: 16 }}>ยังไม่มีใบประกาศในวันนี้</div>
        )}

        {!loading &&
          items.length > 0 &&
          paging.shown.map((item) => (
            <div key={item.id} className="adp-card">
              <div className="adp-thumb">
                {/\.pdf$/i.test(item.imageUrl || "") ? (
                  // a newspaper PDF from "ลงประกาศ" — not a picture, and not used by this PDF generator
                  <a href={fileHref(item.imageUrl)} target="_blank" rel="noreferrer">เปิดไฟล์ PDF ↗<br /><small>(ไม่รวมใน PDF รายวันแบบรูป)</small></a>
                ) : (
                  <img
                    src={fileHref(item.imageUrl)}
                    alt="announce"
                    className="daily-thumb-img"
                    onClick={() => setPreviewImageUrl(fileHref(item.imageUrl))}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                )}
              </div>

              <div className="adp-info">
                <div>รหัส: {item.id}</div>
                <div>IssueDate: {formatDate(item.issueDate?.slice(0, 10))}</div>
                <div>
                  อัปโหลดเมื่อ:{" "}
                  {item.uploadedAt && formatDateTime(item.uploadedAt)}
                </div>

                <div className="adp-btn-row">
                  {dateEdit.id === item.id ? (
                    <>
                      <DateInput
                        value={dateEdit.value}
                        onChange={(e) => setDateEdit({ id: item.id, value: e.target.value })}
                      />
                      <button className="adp-btn-change" onClick={() => handleChangeIssueDate(item.id, dateEdit.value)}>
                        บันทึกวันที่
                      </button>
                      <button onClick={() => setDateEdit({ id: null, value: "" })}>ยกเลิก</button>
                    </>
                  ) : (
                    <button
                      className="adp-btn-change"
                      onClick={() => setDateEdit({ id: item.id, value: (item.issueDate || selectedDate || "").slice(0, 10) })}
                    >
                      เปลี่ยนวันที่เล่ม (ลงย้อนหลัง)
                    </button>
                  )}

                  <button
                    className="adp-btn-delete"
                    onClick={() => handleDeleteAnnouncement(item)}
                  >
                    ลบใบประกาศ
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>
      {!loading && <PageNav paging={paging} />}

      {previewImageUrl && (
        <div className="daily-modal-backdrop" onClick={() => setPreviewImageUrl(null)}>
          <div className="daily-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={previewImageUrl} alt="preview" className="daily-modal-img" />
            <button
              type="button"
              className="daily-modal-close-btn"
              onClick={() => setPreviewImageUrl(null)}
            >
              ปิด
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDailyPdf;
